import { Stack, styled } from "@mui/material";
import { Button, Dialog, DialogContentText, TextField, TextFieldProps } from "components_new";
import { Field, Form, Formik } from "formik";
import React, { FC, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "hooks";

import { DataState } from "store/interfaces";
import { licenseActions } from "store/sections/license";

import { Image } from "types";

import { reportAboutCustomEventInfo } from "utils/reports";

const FieldContainer = styled("div")`
  position: relative;
  padding-bottom: 1rem;
  margin-bottom: 0.5rem;
`;

const Label = styled("label")`
  display: inline-block;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ErrorMsg = styled("div")`
  position: absolute;
  bottom: -2px;
  color: var(--color-red-rose);
  font-size: 12px;
  font-weight: 500;
  margin-left: 0.5rem;
`;

const StyledTextArea = styled((props: TextFieldProps) => <TextField fullWidth multiline minRows={6} {...props} />)(
  ({ theme }) => ({}),
);

const FullWidthTextField = styled((props: TextFieldProps) => <TextField fullWidth {...props} />)(({ theme }) => ({}));

interface FormData {
  subject: string;
  message: string;
  image: Image | null;
}

interface HelpFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialSubject?: string;
}

export const HelpForm: FC<HelpFormProps> = ({ isOpen, onClose, initialSubject = "" }) => {
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const feedback = useAppSelector((state) => state.license.feedback);

  const validate = (values: FormData) => {
    const errors: any = {};

    if (!values.subject) {
      errors.subject = "Required";
    } else if (!values.message) {
      errors.message = "Required";
    }
    return errors;
  };

  const handleChangeFileInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
  ) => {
    const fileReader = new FileReader();
    const image = event?.target?.files?.[0];
    if (image) {
      fileReader.onload = () => {
        if (fileReader.readyState === 2) {
          const res = fileReader.result as string;
          setFieldValue("image", {
            content: res.replace(`data:${image.type};base64,`, ""),
            contentType: image.type,
            fileName: image.name,
          });
        }
      };
      fileReader.readAsDataURL(image);
    }
  };

  const handleSubmit = (values: FormData) => {
    const { subject, message, image } = values;

    dispatch(
      licenseActions.sendFeedback({
        subject,
        body: message,
        url: pathname + search,
        images: image ? [image] : undefined,
      }),
    );
  };

  useEffect(() => {
    if (feedback.data) {
      onClose();
    }

    return () => {
      dispatch(licenseActions.clearFeedback());
    };
  }, [feedback.data, onClose, dispatch]);

  useEffect(() => {
    if (feedback.state === DataState.ERROR) {
      reportAboutCustomEventInfo("feedback_error", { error: feedback.error });
      toast.error("An error occurred while submitting the form, please try again later", {
        position: toast.POSITION.TOP_CENTER,
      });
      toast.clearWaitingQueue();
    }
  }, [feedback.state, feedback.error]);

  return (
    <Dialog maxWidth="sm" title="Customer Feedback" open={isOpen} onClose={onClose}>
      <DialogContentText sx={{ marginY: 2 }}>
        We value your input! Let us know how we can improve by sending us a message with your suggestions, feature
        requests, or bug reports.
      </DialogContentText>
      <Formik<FormData>
        initialValues={{ subject: initialSubject, message: "", image: null }}
        onSubmit={handleSubmit}
        validate={validate}
      >
        {({ errors, touched, setFieldValue }) => (
          <Form>
            <FieldContainer>
              <Label htmlFor="subject">Subject:*</Label>
              <Field as={FullWidthTextField} id="subject" name="subject" type="subject" />
              {errors.subject && touched.subject ? <ErrorMsg>{errors.subject}</ErrorMsg> : null}
            </FieldContainer>
            <FieldContainer>
              <Label htmlFor="message">Message:*</Label>
              <Field as={StyledTextArea} id="message" name="message" />
              {errors.message && touched.message ? <ErrorMsg>{errors.message}</ErrorMsg> : null}
            </FieldContainer>
            <FieldContainer>
              <Label htmlFor="image">Upload image:</Label>
              <Field name="image">
                {({ field, form: { errors }, meta }: any) => (
                  <div>
                    <input
                      id="image"
                      type="file"
                      accept="image/png, image/gif, image/jpeg"
                      onChange={(event) => handleChangeFileInput(event, setFieldValue)}
                    />
                    {meta.touched && meta.error && <ErrorMsg>{errors.message}</ErrorMsg>}
                  </div>
                )}
              </Field>
              {errors.image && touched.image ? <ErrorMsg>{errors.image}</ErrorMsg> : null}
            </FieldContainer>
            <Stack direction="row" justifyContent="flex-end" spacing={2} marginTop={2} marginBottom={2}>
              <Button
                size="medium"
                color="secondary"
                variant="outlined"
                onClick={onClose}
                disabled={feedback.state === DataState.LOADING}
              >
                Cancel
              </Button>
              <Button type="submit" size="medium" color="secondary" loading={feedback.state === DataState.LOADING}>
                Submit
              </Button>
            </Stack>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
